<template>
  <div>
    <div v-if="!loadingIncident">
      <page-header>
        <template #title>
          <h1 class="page-title">
            Claim Monitoring - {{ claimReference }}
            <VulnerabilityLabel v-if="hasVulnerabilities"></VulnerabilityLabel>
          </h1>
        </template>
        <template #actions>
          <incident-download-buttons
            :incident="incident"
            class="btn-group btn-group-sm"
          ></incident-download-buttons>
          <page-header-action
            tag="router-link"
            :to="deploymentPath"
            v-if="!readonly"
          >
            Deployment
          </page-header-action>
          <!--<form-button :indicator="paymentPackIndicator" @clicked="generatePaymentPack">Generate Payment-->
          <!--Pack-->
          <!--</form-button>-->
          <page-header-action @click.prevent="moveToRecovery" v-if="!readonly">
            Move to Recovery
          </page-header-action>
          <page-header-action
            :tag="canSendToVerify ? 'router-link' : 'button'"
            :to="canSendToVerify ? sendToVerifyPath : undefined"
            :disabled="!canSendToVerify || undefined"
            :title="
              !canSendToVerify
                ? 'The Verify integration must be enabled for the TPI to be able to send to Verify'
                : undefined
            "
            v-if="!readonly"
          >
            Send to Verify
          </page-header-action>
          <page-header-action @click.prevent="toggleNotes">
            Notes
          </page-header-action>
          <page-header-action
            variant="danger"
            @click.prevent="reject()"
            v-if="hasPermission('claims.delete') && !readonly"
          >
            Reject
          </page-header-action>
        </template>
      </page-header>

      <claim-handler></claim-handler>

      <div class="alert alert-danger text-white" v-if="!!errors.length">
        <p><strong>Validation Errors</strong></p>
        <ul>
          <li :key="index" v-for="(error, index) in errors">
            {{ error }}
          </li>
        </ul>
      </div>

      <div class="card mb-3">
        <incident-tabs
          :tabs="tabs"
          :source="source"
          :incident="incident"
          :module="module"
          v-on:selected="setActive"
          :active="currentView"
        ></incident-tabs>

        <claim-monitoring
          :incident="incident"
          :module="module"
          :readonly="readonly"
          v-show="currentView == 'claim_monitoring'"
          key="claim_monitoring"
        ></claim-monitoring>

        <incident-diary
          :url="'/claim-monitoring/' + incident"
          :incident="incident"
          :readonly="readonly"
          :slim="true"
          key="diary"
          v-if="currentView == 'diary'"
        ></incident-diary>

        <incident-source
          :incident="incident"
          :showCancel="showCancel"
          :readonly="readonly"
          v-on:created="activateTabs"
          v-on:exists="activateTabs"
          v-if="currentView == 'source'"
          v-on:unsaved-data="saveStatus"
          key="source"
        ></incident-source>

        <accident-details
          :incident="incident"
          :readonly="readonly"
          v-if="currentView == 'accident_description'"
          v-on:unsaved-data="saveStatus"
          key="accidentDetails"
        ></accident-details>

        <client
          :incident="incident"
          :module="module"
          :readonly="readonly"
          v-if="currentView == 'client'"
          v-on:unsaved-data="saveStatus"
          key="client"
        ></client>

        <third-parties
          :incident="incident"
          :module="module"
          :readonly="readonly"
          v-if="currentView == 'third_parties'"
          v-on:unsaved-data="saveStatus"
          key="third_parties"
        ></third-parties>

        <witnesses
          :incident="incident"
          :readonly="readonly"
          v-if="currentView == 'witnesses'"
          key="witnesses"
        ></witnesses>

        <statements
          :incident="incident"
          :readonly="readonly"
          v-if="currentView == 'statements'"
          v-on:unsaved-data="saveStatus"
        ></statements>

        <documents
          :incident="incident"
          :readonly="readonly"
          v-if="currentView === 'documents'"
          key="documents"
        ></documents>

        <jobs
          :incident="source.incident_id"
          v-if="currentView == 'jobs'"
          key="jobs"
        ></jobs>

        <email
          :incident="incident"
          v-if="currentView === 'email'"
          key="email"
        ></email>

        <notes-window
          :show="notes"
          :incident="incident"
          v-on:toggle-note="toggleNotes"
        ></notes-window>
      </div>
    </div>
    <div v-else>
      <preloader></preloader>
    </div>
  </div>
</template>

<script>
import Notes from './../Shared/Notes.vue';
import IncidentDiary from './../Shared/IncidentDiary.vue';
import Source from './../FirstNoticeOfLoss/Sections/Source.vue';
import AccidentDetails from './../FirstNoticeOfLoss/Sections/AccidentDetails.vue';
import Client from './../FirstNoticeOfLoss/Sections/Client.vue';
import ThirdParties from './../FirstNoticeOfLoss/Sections/ThirdParties.vue';
import Witnesses from './../FirstNoticeOfLoss/Sections/Witnesses.vue';
import ClaimMonitoring from './Sections/ClaimMonitoring.vue';
import Documents from './../Shared/Documents.vue';
import PageHeader from './../Shared/PageHeader.vue';
import PageHeaderAction from './../Shared/PageHeaderAction.vue';

import Map from 'lodash/map';
import Email from './../Shared/Email.vue';
import IncidentDownloadButtons from '../Shared/IncidentDownloadButtons';
import IncidentTabs from '../Shared/IncidentTabs';
import Jobs from './../FirstNoticeOfLoss/Sections/Jobs';
import VulnerabilityLabel from '@/components/Modules/Shared/VulnerabilityLabel.vue';
import Statements from '@/components/Modules/FirstNoticeOfLoss/Sections/Statements.vue';

export default {
  props: {
    incident: {
      type: String,
      required: true
    }
  },

  components: {
    VulnerabilityLabel,
    Statements,
    PageHeader,
    PageHeaderAction,
    'notes-window': Notes,
    Documents,
    IncidentDiary,
    'incident-source': Source,
    AccidentDetails,
    Client,
    ThirdParties,
    Witnesses,
    ClaimMonitoring,
    IncidentTabs,
    Email,
    Jobs,
    IncidentDownloadButtons
  },

  data() {
    return {
      module: 'Claim Monitoring',
      paymentPackIndicator: false,
      updatingType: false,
      type: null,
      notes: false,
      showCancel: false,
      currentView: 'claim_monitoring',
      claim_types: [],
      available_dates: [],
      reasons: [],
      hire: {},
      hire_monitoring: {},
      referringToAgent: false,
      downloading: false,
      tabs: [
        {
          name: 'claim_monitoring',
          label: 'Claim Monitoring'
        },
        {
          name: 'diary',
          label: 'Diary',
          disabled: false
        },
        {
          name: 'jobs',
          label: 'Jobs',
          disabled: false
        }
      ],
      loaded: false,
      unsaved_data: false,
      liabilityType: null,
      incident_date: null,
      errors: []
    };
  },

  computed: {
    deploymentPath() {
      return `/deployment/${this.incident}/`;
    },

    readonly() {
      return this.isReadonly();
    },

    sendToVerifyPath() {
      return `/claim-monitoring/${this.incident}/send-to-verify`;
    },

    claimReference() {
      const source = this.$store.getters['Incident/source'];

      if (source && source.claim_reference) {
        return source.claim_reference;
      }

      return null;
    },

    source() {
      return this.$store.getters['Incident/source'];
    },

    loadingIncident() {
      return this.$store.getters['Incident/loading'];
    },

    canSendToVerify() {
      const thirdParties = this.$store.getters['Incident/thirdParties'];
      return thirdParties?.some(tp => tp.insurer?.verify_code);
    },

    hasVulnerabilities() {
      const vulnerabilities =
        this.$store.getters['Incident/vulnerabilities'] || [];

      return vulnerabilities.length > 0;
    }
  },

  mounted() {
    this.$store.commit('Tariffs/retrieve');
    this.$store.dispatch('Incident/retrieve', this.incident);

    window.Ajax.get(`/incidents/reasons?filter[status]=rejected`)
      .then(response => {
        this.reasons = response.data;
      })
      .catch(error => {});
  },

  methods: {
    referToAgentCheck() {
      window
        .swal({
          title: 'Are you sure?',
          text: `This will instruct the agent to manage this case for you`,
          type: 'warning',
          showCancelButton: true
        })
        .then(() => {
          let agent = this.$store.getters.agent;
          if (agent) {
            this.referringToAgent = true;
            window.Ajax.post(
              `/incidents/${this.incident}/instruct-agent/${agent.identifier}`
            )
              .then(response => {
                window.Ajax.post(
                  `/first-notice-of-loss/${this.incident}/complete`
                )
                  .then(response => {
                    window.toastify.success(
                      'Success!',
                      'The agent was instructed successfully!'
                    );
                    this.$router.push('/claim-monitoring');
                    this.referringToAgent = false;
                  })
                  .catch(error => {
                    this.referringToAgent = false;
                    window.toastify.error(
                      'Error',
                      "Sorry, we couldn't instruct the agent"
                    );
                  });
              })
              .catch(error => {
                this.referringToAgent = false;
                window.toastify.error(
                  'Error',
                  "Sorry, we couldn't instruct the agent"
                );
              });
          }
        })
        .catch(error => {});
    },

    moveToRecovery() {
      if (this.readonly) return;
      // If the user does not have permission to update a claim then just redirect to recovery
      if (!this.hasPermission('claims.update')) {
        this.route(`/recovery/${this.incident}`);
        return;
      }

      this.errors = [];

      window
        .swal({
          title: 'Are you sure?',
          text: 'Are you sure you want to move this claim to recovery?',
          type: 'warning',
          showCancelButton: true
        })
        .then(
          () => {
            window.Ajax.post(`/claim-monitoring/${this.incident}`)
              .then(response => {
                this.route(`/recovery/${this.incident}`);
              })
              .catch(error => {
                if (error.response.status === 422) {
                  this.errors = Object.values(
                    error.response.data.errors
                  ).flat();

                  window.toastify.error(
                    'Error',
                    'Sorry, there was an issue with the hire dates, please fix the errors and then try again.'
                  );
                } else {
                  window.toastify.error(
                    'Error',
                    'Sorry, there was an issue moving the incident to recovery.'
                  );
                }
              });
          },
          dismiss => {}
        );
    },

    generatePaymentPack() {
      if (this.readonly) return;
      this.paymentPackIndicator = true;
      window.Ajax.post(
        `/claim-monitoring/${this.incident}/storage-recovery-invoice`
      )
        .then(response => {
          this.route('/claim-monitoring/' + this.incident + '/payment-pack');
        })
        .catch(error => {
          this.paymentPackIndicator = false;
          window.toastify.error(
            'Error',
            'Sorry, there was an issue generating the payment pack.'
          );
        });
    },

    setClaimType(alert = true) {
      if (this.available_dates.length) {
        this.showPrompt(alert);
      } else {
        this.makeClaimTypeRequest(alert);
      }
    },

    setActive(tab) {
      // check if unsaved data on the current form
      if (this.unsaved_data && this.currentView !== tab) {
        this.saveWarning(tab); // show alert if there is
      } else {
        this.currentView = tab;
      }
    },

    saveStatus() {
      this.unsaved_data = true;
    },

    saveWarning(tab) {
      window
        .swal({
          title: 'Are you sure?',
          text:
            'You will lose all details entered in this section if you continue!',
          type: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Continue without saving',
          cancelButtonText: 'Cancel and stay in this section'
        })
        .then(
          () => {
            // Go to selected tab discarding changes
            this.currentView = tab;
            this.unsaved_data = false;
          },
          dismiss => {}
        );
    },

    toggleNotes() {
      this.notes = !this.notes;
    },

    reject() {
      if (this.readonly) return;
      const options = {};

      this.reasons.forEach((reason, key) => {
        options[key] = reason.body;
      });

      window
        .swal({
          title: 'Please Confirm',
          text: 'Are you sure you want reject this claim?',
          type: 'warning',
          input: 'select',
          inputOptions: options,
          inputPlaceholder: 'Select a Reason',
          inputValidator: value => {
            return new Promise((resolve, reject) => {
              if (value) {
                resolve(value);
              } else {
                reject('You must select a reason.');
              }
            });
          },
          showCancelButton: true,
          confirmButtonText: 'Yes',
          cancelButtonText: 'No'
        })
        .then(reason => {
          const reasonId = this.reasons[reason]?.id;

          if (reasonId) {
            window.Ajax.delete(`/incidents/${this.incident}?reason=${reasonId}`)
              .then(response => {
                window.toastify.success(
                  'Success',
                  'The claim was rejected successfully!'
                );
                this.route(`/claim-monitoring`);
              })
              .catch(error => {
                window.toastify.error(
                  'Error',
                  'Sorry, there was an issue rejecting this claim.'
                );
              });
          }
        })
        .catch(() => {});
    }
  },

  watch: {
    source(source) {
      this.incident_date = source.incident_occurred_at;
    }
  },

  beforeDestroy() {
    this.$store.commit('Incident/clearRecord');
  }
};
</script>

<style scoped>
.details-row {
  display: flex;
  justify-content: space-between;
}

.details-row .panel {
  width: 23%;
}
</style>
